import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ServicesAccordion = () => {
  const { t } = useTranslation();
  const [openCards, setOpenCards] = useState([]);

  // Colonne gauche : Sparring, Pilote, Recruteur
  const leftColumn = [
    {
      id: 'sparring',
      href: '/services/sparring',
      titleKey: 'services.sparring.title',
      descriptionKey: 'services.sparring.description',
      items: [
        { icon: '🎯', labelKey: 'services.sparring.items.vision' },
        { icon: '⚡', labelKey: 'services.sparring.items.prioritisation' },
        { icon: '🧭', labelKey: 'services.sparring.items.arbitrage' },
        { icon: '📊', labelKey: 'services.sparring.items.review' },
        { icon: '🔍', labelKey: 'services.sparring.items.analysis' },
        { icon: '💡', labelKey: 'services.sparring.items.innovation' },
      ]
    },
    {
      id: 'pilote',
      href: '/services/pilote',
      titleKey: 'services.pilote.title',
      descriptionKey: 'services.pilote.description',
      items: [
        { icon: '🚀', labelKey: 'services.pilote.items.deployment' },
        { icon: '📊', labelKey: 'services.pilote.items.kpis' },
        { icon: '🎯', labelKey: 'services.pilote.items.objectives' },
        { icon: '📅', labelKey: 'services.pilote.items.planning' },
        { icon: '🔄', labelKey: 'services.pilote.items.iterations' },
        { icon: '💰', labelKey: 'services.pilote.items.revenue' },
      ]
    },
    {
      id: 'recruteur',
      href: '/services/recruteur',
      titleKey: 'services.recruteur.title',
      descriptionKey: 'services.recruteur.description',
      items: [
        { icon: '🔎', labelKey: 'services.recruteur.items.sourcing' },
        { icon: '✅', labelKey: 'services.recruteur.items.selection' },
        { icon: '🎯', labelKey: 'services.recruteur.items.matching' },
        { icon: '📋', labelKey: 'services.recruteur.items.assessment' },
        { icon: '🚀', labelKey: 'services.recruteur.items.onboarding' },
        { icon: '📈', labelKey: 'services.recruteur.items.rampup' },
      ]
    },
  ];

  // Colonne droite : Architecte, Coach, Accélérateur
  const rightColumn = [
    {
      id: 'architecte',
      href: '/services/architecte',
      titleKey: 'services.architecte.title',
      descriptionKey: 'services.architecte.description',
      items: [
        { icon: '🏗️', labelKey: 'services.architecte.items.organisation' },
        { icon: '⚙️', labelKey: 'services.architecte.items.process' },
        { icon: '🛠️', labelKey: 'services.architecte.items.stack' },
        { icon: '📝', labelKey: 'services.architecte.items.pitch' },
        { icon: '🔄', labelKey: 'services.architecte.items.funnels' },
        { icon: '📈', labelKey: 'services.architecte.items.scalability' },
      ]
    },
    {
      id: 'coach',
      href: '/services/coach',
      titleKey: 'services.coach.title',
      descriptionKey: 'services.coach.description',
      items: [
        { icon: '📞', labelKey: 'services.coach.items.prospection' },
        { icon: '🔍', labelKey: 'services.coach.items.discovery' },
        { icon: '🤝', labelKey: 'services.coach.items.closing' },
        { icon: '💬', labelKey: 'services.coach.items.negotiation' },
        { icon: '🎓', labelKey: 'services.coach.items.training' },
        { icon: '📈', labelKey: 'services.coach.items.performance' },
      ]
    },
    {
      id: 'accelerateur',
      href: '/services/accelerateur',
      titleKey: 'services.accelerateur.title',
      descriptionKey: 'services.accelerateur.description',
      items: [
        { icon: '🔬', labelKey: 'services.accelerateur.items.diagnostic' },
        { icon: '⚡', labelKey: 'services.accelerateur.items.optimisation' },
        { icon: '📊', labelKey: 'services.accelerateur.items.conversion' },
        { icon: '🚀', labelKey: 'services.accelerateur.items.scaling' },
        { icon: '💰', labelKey: 'services.accelerateur.items.revenue' },
        { icon: '🔄', labelKey: 'services.accelerateur.items.quickwins' },
      ]
    },
  ];

  const toggleCard = (id) => {
    setOpenCards(prev => 
      prev.includes(id) 
        ? prev.filter(cardId => cardId !== id)
        : [...prev, id]
    );
  };

  // Composant carte réutilisable
  const ServiceCard = ({ service }) => {
    const isOpen = openCards.includes(service.id);
    
    return (
      <div
        className={`bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${
          isOpen ? 'shadow-xl' : 'shadow-sm hover:shadow-md'
        }`}
      >
        {/* Header de la carte - toujours visible */}
        <button
          onClick={() => toggleCard(service.id)}
          className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
        >
          <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 pr-2">
            {t(service.titleKey)}
          </h3>
          <div 
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
              isOpen ? 'bg-[#111827] rotate-45' : 'bg-[#2A41E5]'
            }`}
          >
            <svg 
              className="w-4 h-4 sm:w-5 sm:h-5 text-white" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        </button>

        {/* Contenu dépliable avec animation fluide */}
        <div 
          className={`grid transition-all duration-500 ease-in-out ${
            isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="px-4 sm:px-6 pb-4 sm:pb-6">
              {/* Description */}
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                {t(service.descriptionKey)}
              </p>

              {/* Items */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {service.items.map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 sm:gap-3 text-gray-700"
                  >
                    <span className="text-[#2A41E5] text-sm sm:text-base">{item.icon}</span>
                    <span className="font-medium text-sm sm:text-base">{t(item.labelKey)}</span>
                  </div>
                ))}
              </div>

              {/* Lien En savoir plus */}
              <Link 
                to={service.href}
                className="inline-flex items-center gap-2 mt-4 sm:mt-6 text-[#2A41E5] font-semibold hover:underline text-sm sm:text-base"
              >
                {t('services.learnMore')}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="services-accordion" className="py-12 sm:py-16 md:py-24 px-4 md:px-8 bg-[#041C2C]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            {t('services.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-2">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Deux colonnes indépendantes */}
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-start">
          {/* Colonne gauche */}
          <div className="w-full lg:flex-1 flex flex-col gap-3 sm:gap-4">
            {leftColumn.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>

          {/* Colonne droite */}
          <div className="w-full lg:flex-1 flex flex-col gap-3 sm:gap-4">
            {rightColumn.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesAccordion;
